import { useState, useRef } from 'react';
import {
  UtensilsCrossed,
  Coffee,
  Pizza,
  Sandwich,
  IceCream,
  Beef,
  Cherry,
  Croissant,
  CupSoda,
  Soup,
  Wine,
  Beer,
  Upload,
  X,
  Loader2,
  ImageIcon,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

// Food-related icons with labels
const ICON_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed },
  { value: 'coffee', label: 'Coffee', icon: Coffee },
  { value: 'pizza', label: 'Pizza', icon: Pizza },
  { value: 'sandwich', label: 'Sandwich', icon: Sandwich },
  { value: 'icecream', label: 'Ice Cream', icon: IceCream },
  { value: 'steak', label: 'Steak', icon: Beef },
  { value: 'dessert', label: 'Dessert', icon: Cherry },
  { value: 'bakery', label: 'Bakery', icon: Croissant },
  { value: 'drinks', label: 'Drinks', icon: CupSoda },
  { value: 'soup', label: 'Soup', icon: Soup },
  { value: 'wine', label: 'Wine Bar', icon: Wine },
  { value: 'beer', label: 'Bar', icon: Beer },
];

// Accent color options
const ACCENT_COLORS = [
  { color: '#f97316', name: 'Orange' },
  { color: '#3b82f6', name: 'Blue' },
  { color: '#22c55e', name: 'Green' },
  { color: '#a855f7', name: 'Purple' },
  { color: '#ec4899', name: 'Pink' },
  { color: '#14b8a6', name: 'Teal' },
  { color: '#ef4444', name: 'Red' },
  { color: '#eab308', name: 'Yellow' },
];

interface LocationIconSelectorProps {
  icon?: string | null;
  imageUrl?: string | null;
  accentColor?: string | null;
  onIconChange: (icon: string | null) => void;
  onImageChange: (imageUrl: string | null) => void;
  onAccentColorChange?: (color: string | null) => void;
  organizationId?: string;
}

export function LocationIconSelector({
  icon,
  imageUrl,
  accentColor,
  onIconChange,
  onImageChange,
  onAccentColorChange,
  organizationId,
}: LocationIconSelectorProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleIconSelect = (iconValue: string) => {
    onIconChange(iconValue);
    // Clear image when selecting an icon
    if (imageUrl) {
      onImageChange(null);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image must be less than 2MB');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `location-${Date.now()}.${fileExt}`;
      const filePath = organizationId ? `${organizationId}/${fileName}` : fileName;

      // Upload to Supabase Storage (logos bucket)
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      onImageChange(publicUrl);
      // Clear icon when uploading image
      if (icon) {
        onIconChange(null);
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    onImageChange(null);
  };

  const handleClear = () => {
    onIconChange(null);
    onImageChange(null);
  };

  return (
    <div className="space-y-4">
      <Label>Location Icon</Label>

      <Tabs defaultValue={imageUrl ? 'image' : 'icon'} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="icon" className="gap-2">
            <UtensilsCrossed className="h-4 w-4" />
            Icon
          </TabsTrigger>
          <TabsTrigger value="image" className="gap-2">
            <ImageIcon className="h-4 w-4" />
            Image
          </TabsTrigger>
        </TabsList>

        <TabsContent value="icon" className="space-y-3 pt-2">
          <div className="grid grid-cols-6 gap-1.5">
            {ICON_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleIconSelect(option.value)}
                  className={cn(
                    'flex flex-col items-center gap-0.5 p-2 rounded-lg border transition-all',
                    icon === option.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-transparent bg-muted/50 hover:bg-muted hover:border-muted-foreground/20'
                  )}
                  title={option.label}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-[9px] leading-tight truncate w-full text-center">{option.label}</span>
                </button>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="image" className="space-y-3 pt-2">
          {imageUrl ? (
            <div className="relative">
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <img
                  src={imageUrl}
                  alt="Location"
                  className="h-12 w-12 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">Custom Image</p>
                  <p className="text-xs text-muted-foreground">Click remove to change</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveImage}
                  className="shrink-0 h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div
              className={cn(
                'flex flex-col items-center justify-center gap-1.5 p-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                'hover:border-primary/50 hover:bg-muted/50'
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-6 w-6 text-muted-foreground" />
              )}
              <div className="text-center">
                <p className="text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Click to upload'}
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG up to 2MB
                </p>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />

          {uploadError && (
            <p className="text-sm text-red-500">{uploadError}</p>
          )}
        </TabsContent>
      </Tabs>

      {(icon || imageUrl) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="text-muted-foreground h-7 px-2"
        >
          <X className="h-3 w-3 mr-1" />
          Clear
        </Button>
      )}

      {/* Accent Color Picker */}
      {onAccentColorChange && (
        <div className="space-y-2 pt-2 border-t">
          <Label>Location Accent Color</Label>
          <div className="flex gap-2 flex-wrap">
            {ACCENT_COLORS.map((option) => (
              <button
                key={option.color}
                type="button"
                onClick={() => onAccentColorChange(option.color)}
                className={cn(
                  'w-8 h-8 rounded-full ring-2 ring-offset-2 ring-offset-background transition-all relative',
                  accentColor === option.color ? 'ring-foreground' : 'ring-transparent hover:ring-muted'
                )}
                style={{ backgroundColor: option.color }}
                title={option.name}
              >
                {accentColor === option.color && (
                  <Check className="h-3 w-3 text-white absolute inset-0 m-auto" />
                )}
              </button>
            ))}
            {accentColor && (
              <button
                type="button"
                onClick={() => onAccentColorChange(null)}
                className="w-8 h-8 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center hover:border-muted-foreground/50 transition-colors"
                title="Clear color"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Optional: Set a unique accent color for this location
          </p>
        </div>
      )}
    </div>
  );
}
